<template>
  <div>
    <my-header></my-header>
  
    <div class="content-container-level-1">
      <h2>New contest</h2>

      <form
        v-if="isLoadedNormal"
        @submit.prevent="createContestsWithConfirmation"
      >
        <table class="contest-table">
          <tr>
            <th>
              <label for="type-selector">Type</label>
            </th>
            <td colspan="2">
              <b-form-select
                id="type-selector"
                v-model="type"
                :options="typeOptions"
              />
            </td>
          </tr>
          <tr>
            <th class="text-nowrap">Segment</th>
            <td colspan="2">
              <b-form-select
                id="player-group-selector"
                v-model="playerGroupId"
                :options="playerGroupOptions"
              />
            </td>
          </tr>

          <period-input-tbody
            v-model="firstPeriod"
            :beginMin="minPlannedStartDate"
            :endMin="minPlannedFinishDate"
            class="period"
          />

          <tr>
            <th>Duration</th>
            <td colspan="2" class="event-duration">{{durationStr}}</td>
          </tr>
        </table>

        <hr>

        <template v-if="isGroupCreationRuleSupported">
          <div class="form-group">
            <label for="group-creation-rule-selector">Правило создания новых групп</label>
            <b-form-select
              id="group-creation-rule-selector"
              v-model="groupCreationRule"
              :options="groupCreationRuleOptions"
              class="mb-2"
            />

            <p style="white-space:pre-line">{{groupCreationRuleDescription}}</p>
          </div>

          <hr>
        </template>

        <template v-if="isClientMinVersionSupported">
          <h5>Client min version</h5>
          <client-version
            :clientVersionInfo="clientVersionInfo"
            class="mb-2"
          />

          <p class="mb-2">
            {{clientMinVersionComment}}
          </p>
          <input class="form-control" v-model="clientMinVersion" required>

          <hr>
        </template>

        <h5>
          <b-form-checkbox v-model="isRepeatRequired">
            Повторять по правилам
          </b-form-checkbox>
        </h5>

        <template v-if="isRepeatRequired">
          <div class="mb-3">
            <label for="pause" class="mb-1">Пауза между конкурсами (в часах)</label>
            <input id="pause" type="number" min="0" class="form-control" v-model="pauseHourStr">
          </div>

          <p class="mb-1">Повторять до даты</p>
          <moment-input-table
            v-model="repeatEndMoment"
            :min="repeatEndMomentMin"
            class="mb-3"
          />

          <template v-if="optionalPeriods !== null">
            <p class="mb-1">Все конкурсы, которые будут созданы.</p>
            <periods-table
              :periods="optionalPeriods"
            />
          </template>

        </template>

        <hr>

        <b-button :to="{name: 'contests'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import notifier from '../notifier.js';

import { HTTP } from '../http-common.js';

/**
 * @typedef { import('../model/contest.js').ContestType } ContestType
 */
import { CONTEST_TYPE_INDIVIDUAL      } from '../model/contest.js';
import { getContestTypeOptionsFull    } from '../model/contest.js';
import { isClientMinVersionSupported  } from '../model/contest.js';

/**
 * @typedef { import('../model/contest.js').GroupCreationRule } GroupCreationRule
 */
import { GROUP_CREATION_RULE__DYNAMIC    } from '../model/contest.js';
import { isGroupCreationRuleSupported    } from '../model/contest.js';
import { getGroupCreationRuleOptions     } from '../model/contest.js';
import { getGroupCreationRuleDescription } from '../model/contest.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { ClientVersionInfo } from '../model/version.js';

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { DEFAULT_PLAYER_GROUP_ID      } from '../model/event.js';
import { getPlayerGroupOptionsVerbose } from '../model/event.js';

/**
 * @typedef { import('../time.js').DatePeriodObject } DatePeriodObject
 */
import { DatePeriodObject_getDurationStr } from '../time.js';

import { normalizeVersionStr  } from '../util.js';
import { floorMomentTo5Min    } from '../util.js';
import { Period               } from '../util.js';
import { parseUint32          } from '../util.js';
import { convertHourToSec     } from '../util.js';
import { convertMomentToStamp } from '../util.js';
import { getRepeatedPeriods   } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import ClientVersion from './ClientVersion.vue';

import PeriodInputTbody from './util/PeriodInput.vue';
import MomentInputTable from './util/MomentInputTable.vue';
import PeriodsTable from './PeriodsTable.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'ContestAdd',

  components: {
    MyHeader,
    LoadingIndicator,
    ClientVersion,
    PeriodInputTbody,
    MomentInputTable,
    PeriodsTable,
  },

  data() {
    const now = new Date();
    const begin = floorMomentTo5Min(now);

    // By default contest duration is 60 hours (2 days 12 hours).
    const DEFAULT_DURATION_HOURS = 60;

    const end = new Date(begin);
    end.setHours(end.getHours() + DEFAULT_DURATION_HOURS);

    /** @type {DatePeriodObject} */
    const firstPeriod = {
      begin: begin,
      end:   end,
    };

    return {
      loadState: LOAD_STATE_INIT,

      /** @type {?ClientVersionInfo} */
      clientVersionInfo: null,

      type: CONTEST_TYPE_INDIVIDUAL,

      typeOptions: getContestTypeOptionsFull(),

      firstPeriod: firstPeriod,
      minPlannedStartDate:  begin,
      minPlannedFinishDate: begin,

      clientMinVersion: '',

      playerGroupId:      DEFAULT_PLAYER_GROUP_ID,
      playerGroupOptions: getPlayerGroupOptionsVerbose(),

      /** @type {GroupCreationRule} */
      groupCreationRule:        GROUP_CREATION_RULE__DYNAMIC,
      groupCreationRuleOptions: getGroupCreationRuleOptions(),

      isRepeatRequired: false,
      pauseHourStr: '24',

      repeatEndMoment:    end,
      repeatEndMomentMin: begin,
    };
  },

  computed: {
    /** @returns {boolean} */
    isClientMinVersionSupported() {
      return isClientMinVersionSupported(this.type);
    },

    /** @returns {boolean} */
    isGroupCreationRuleSupported() {
      return isGroupCreationRuleSupported(this.type);
    },

    /** @returns {?string} */
    groupCreationRuleDescription() {
      return getGroupCreationRuleDescription(this.groupCreationRule);
    },

    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {string} */
    durationStr() {
      return DatePeriodObject_getDurationStr(this.firstPeriod);
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const clientVersionInfo = this.clientVersionInfo;
      if(clientVersionInfo === null) {
        return null;
      }

      if(clientVersionInfo.gameVersion === clientVersionInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new contest must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + clientVersionInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new contest inside this range.';
    },

    /** @returns {?Period[]} */
    optionalPeriods() {
      const [periods, err] = this.periods;
      if(err !== null) {
        return null;
      }
      return periods;
    },

    /** @returns {[?Period[], ?Error]} */
    periods() {
      /** @type {?Period} */
      let firstPeriod;

      /** @type {?Error} */
      let err;

      [firstPeriod, err] = this.validFirstStampPeriod;
      if(err !== null) {
        return [null, err];
      }
      if(firstPeriod === null) {
        return [null, new Error('firstPeriod === null')];
      }

      if(!this.isRepeatRequired) {
        const periods = [firstPeriod];
        return [periods, null];
      }

      /** @type {?number} */
      let pauseSec;

      [pauseSec, err] = this.pauseSec;
      if(err !== null) {
        return [null, err];
      }
      if(pauseSec === null) {
        return [null, new Error('pauseSec === null')];
      }

      /** @type {?number} */
      let repeatEndStamp;
      [repeatEndStamp, err] = this.repeatEndStamp;
      if(err !== null) {
        return [null, err];
      }
      if(repeatEndStamp === null) {
        return [null, new Error('repeatEndStamp === null')];
      }

      if(firstPeriod.end > repeatEndStamp) {
        return [null, new Error('First period end must be before repeat end.')];
      }

      const periods = getRepeatedPeriods(firstPeriod, pauseSec, repeatEndStamp);
      return [periods, null];
    },

    /** @returns {[?Period, ?Error]} */
    validFirstStampPeriod() {
      const firstDatePeriod = this.firstPeriod;
      const begin = convertMomentToStamp(firstDatePeriod.begin);
      const end   = convertMomentToStamp(firstDatePeriod.end);

      if(end === begin) {
        return [null, new Error('Contest duration is zero.')];
      }
      if(end <= begin) {
        return [null, new Error('End time must be after begin time.')];
      }

      return [new Period(begin, end), null];
    },

    /** @returns {[?number, ?Error]} */
    pauseHour() {
      const pauseHour = parseUint32(this.pauseHourStr);
      if(pauseHour === null) {
        return [null, new Error('Pause between events is invalid.')];
      }
      return [pauseHour, null];
    },

    /** @returns {[?number, ?Error]} */
    pauseSec() {
      const [pauseHour, err] = this.pauseHour;
      if(err !== null) {
        return [null, err];
      }
      if(pauseHour === null) {
        return [null, new Error('pauseHour === null')];
      }

      const pauseSec = convertHourToSec(pauseHour);
      return [pauseSec, null];
    },

    /** @returns {[?number, ?Error]} */
    repeatEndStamp() {
      const repeatEndMoment = this.repeatEndMoment;
      if(repeatEndMoment === null) {
        return [null, new Error('Repeat end moment is invalid.')];
      }

      const stamp = convertMomentToStamp(repeatEndMoment);
      return [stamp, null];
    },
  },

  created() {
    this.loadNewContestInfo();
  },

  methods: {
    loadNewContestInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/new_contest_info')
      .then(response => {
        /** @type {NewContestInfo} */
        const data = response.data.data;

        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const gameVersion = normalizeVersionStr(data.gameVersion);
        if(gameVersion === null) {
          notifier.showError('Loaded gameVersion is invalid.', {sender: this});
          return;
        }

        const forcedVersion = data.forcedVersion;
        this.clientVersionInfo = new ClientVersionInfo(gameVersion, forcedVersion);

        this.clientMinVersion = forcedVersion;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    createContestsWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const [contestsDef, err] = this.getContestsDef();
      if(err !== null) {
        notifier.showError(err, {sender: this});
        return;
      }
      if(contestsDef === null) {
        notifier.showError('Invalid input report. contestsDef === null.', {sender: this});
        return;
      }

      const contestAmount = contestsDef.periods.length;
      this.confirmContestsCreation(
        contestAmount,
        () => {
          this.createContests(contestsDef);
        }
      );
    },

    /**
     * @returns {[?ContestsDef, ?Error]}
     */
    getContestsDef() {
      let clientMinVersionOrNull = null;

      if(this.isClientMinVersionSupported) {
        const trimmedClientMinVersion = this.clientMinVersion.trim();
        if(trimmedClientMinVersion.length === 0) {
          return [null, new Error('Client min version must be specified.')];
        }
        clientMinVersionOrNull = trimmedClientMinVersion;
      }

      const [periods, err] = this.periods;
      if(err !== null) {
        return [null, err];
      }
      if(periods === null) {
        return [null, new Error('periods === null')];
      }

      const contestsDef = new ContestsDef(
        this.type,
        clientMinVersionOrNull,
        this.playerGroupId,
        periods
      );

      if(isGroupCreationRuleSupported(this.type)) {
        contestsDef.groupCreationRule = this.groupCreationRule;
      }

      return [contestsDef, null];
    },

    /**
     * @param {number} contestAmount
     * @param {ConfirmHandle} onConfirmed
     */
    confirmContestsCreation(contestAmount, onConfirmed) {
      const target = (contestAmount === 1) ? 'this contest' : 'these contests';

      confirmAction(
        this,
        `Are you sure you want to create ${target} {env}?`,
        'Create',
        onConfirmed,
      );
    },

    /**
     * @param {ContestsDef} contestsDef
     */
    createContests(contestsDef) {
      HTTP.post('/contests/add', contestsDef)
      .then(response => {
        const result = response.data.result.code;

        const RESULT_SUCCESS    = 1;
        const RESULT_HAS_ACTIVE = 2;

        if(result === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Contest created.');
          this.$router.push({name: 'contests'});
          return;
        }

        if(result === RESULT_HAS_ACTIVE) {
          notifier.showError('Can not start contest. Contest with specified type is already active.', {sender: this});
          return;
        }

        notifier.showError(`Unknown result code ${result}.`, {sender: this});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};

/**
 * @typedef {Object} NewContestInfo
 * @property {string} gameVersion
 * @property {string} forcedVersion
 */


class ContestsDef {
  /**
   * @param {ContestType}   type
   * @param {?string}       clientMinVersion
   * @param {PlayerGroupId} playerGroupId
   * @param {Period[]}      periods
   */
  constructor(
    type,
    clientMinVersion,
    playerGroupId,
    periods
  ) {
    this.type             = type;
    this.clientMinVersion = clientMinVersion;
    this.playerGroupId    = playerGroupId;
    this.periods          = periods;
  }
}
</script>

<style scoped>
.contest-table {
  width: 100%;
}

.contest-table > tr > th {
  width: 0%;
  padding-right: .5rem;
}

.contest-table > tr > th > label {
  margin-bottom: 0;
}

.contest-table > tr:not(:last-child) >>> th,
.contest-table > tr:not(:last-child) >>> td {
  padding-bottom: .6rem;
}

.period >>> tr.end > th,
.period >>> tr.end > td {
  padding-bottom: .6rem;
}

.event-duration {
  padding-left: 0.8rem;
}
</style>
